import { CacheProvider } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import { NonceProvider } from '@shopify/hydrogen';
import { startTransition, StrictMode, useMemo, useState } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';
import { ClientStyleContext } from './components/providers/ClientStyleContext';
import { LeafioThemeProvider } from './components/providers/LeafioThemeProvider';
import { getLogger } from './lib/logger/logger';
import { createEmotionCache } from './utils/createEmotionCache';

const logger = getLogger('EntryClient');

function ClientCacheProvider({ children }: { children: React.ReactNode }) {
  const [cache, setCache] = useState(createEmotionCache());

  const clientStyleContextValue = useMemo(
    () => ({
      reset() {
        setCache(createEmotionCache());
      },
    }),
    [],
  );

  return (
    <ClientStyleContext.Provider value={clientStyleContextValue}>
      <CacheProvider value={cache}>
        <LeafioThemeProvider>
          <CssBaseline />
          {children}
        </LeafioThemeProvider>
      </CacheProvider>
    </ClientStyleContext.Provider>
  );
}

const hydrate = () => {
  // Prevent hydration when viewed through Google's cache
  if (window.location.origin.includes('webcache.googleusercontent.com')) {
    return;
  }

  startTransition(() => {
    // Extract nonce from existing script tags for CSP compliance
    const existingNonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;

    hydrateRoot(
      document.getElementById('root')!,
      <StrictMode>
        <NonceProvider value={existingNonce}>
          <ClientCacheProvider>
            <HydratedRouter />
          </ClientCacheProvider>
        </NonceProvider>
      </StrictMode>,
    );
  });
};

// Register service worker
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/service-worker.js')
    .then(() => {
      navigator.serviceWorker.ready.then(() => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'SET_PROXY_HOST',
            proxyHost: window.location.origin,
          });
        }
      });
    })
    .catch((error) => {
      logger.error('Service worker registration failed', { error });
    });
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  window.setTimeout(hydrate, 1);
}
